import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { DashboardLayout } from '@/components/dashboard/DashboardLayout'
import { SubmissionList } from '@/components/dashboard/SubmissionList'
import { useSubmissionCounts, useCreateSubmission } from '@/hooks/useSubmissions'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { FileText, Clock, CheckCircle, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'

const createSubmissionSchema = z.object({
  insured_name: z.string().min(2, 'Name must be at least 2 characters')
})

type CreateSubmissionForm = z.infer<typeof createSubmissionSchema>

export default function Dashboard() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const currentTab = searchParams.get('tab') || 'overview'
  const [showNewSubmission, setShowNewSubmission] = useState(false)
  const { data: counts, isLoading: countsLoading } = useSubmissionCounts()
  const createMutation = useCreateSubmission()

  const form = useForm<CreateSubmissionForm>({
    resolver: zodResolver(createSubmissionSchema),
    defaultValues: { insured_name: '' }
  })

  const onSubmit = async (data: CreateSubmissionForm) => {
    try {
      const submission = await createMutation.mutateAsync(data)
      toast.success('Submission created')
      setShowNewSubmission(false)
      form.reset()
      navigate(`/workspace/${submission.id}`)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create submission'
      toast.error(errorMessage)
      console.error('Submission creation error:', err)
    }
  }

  const stats = [
    {
      title: 'Total Submissions',
      value: counts?.all || 0,
      icon: FileText,
      color: 'text-blue-600'
    },
    {
      title: 'In Progress',
      value: (counts?.draft || 0) + (counts?.processing || 0),
      icon: Clock,
      color: 'text-yellow-600'
    },
    {
      title: 'Completed',
      value: counts?.complete || 0,
      icon: CheckCircle,
      color: 'text-green-600'
    },
    {
      title: 'Needs Review',
      value: counts?.review || 0,
      icon: AlertCircle,
      color: 'text-orange-600'
    }
  ]

  return (
    <DashboardLayout onNewSubmission={() => setShowNewSubmission(true)}>
      <div className="p-8">
        {currentTab === 'overview' ? (
          <>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold">Dashboard</h1>
              <p className="text-muted-foreground">
                Manage your insurance submissions and risk assessments
              </p>
            </div>

            {/* Stats */}
            <div className="mb-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {stats.map((stat) => (
                <Card key={stat.title}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {stat.title}
                    </CardTitle>
                    <stat.icon className={`h-4 w-4 ${stat.color}`} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {countsLoading ? '-' : stat.value}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Recent Submissions */}
            <div>
              <h2 className="mb-4 text-xl font-semibold">Recent Submissions</h2>
              <SubmissionList />
            </div>
          </>
        ) : (
          <>
            {/* Submissions Tab - Full page submissions view */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold">Submissions</h1>
              <p className="text-muted-foreground">
                View and manage all your insurance submissions
              </p>
            </div>

            <SubmissionList />
          </>
        )}
      </div>

      {/* New Submission Dialog */}
      <Dialog open={showNewSubmission} onOpenChange={setShowNewSubmission}>
        <DialogContent>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>Create New Submission</DialogTitle>
              <DialogDescription>
                Start a new insurance risk assessment by entering the insured
                party&apos;s name.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="space-y-2">
                <Label htmlFor="insured_name">Insured Name</Label>
                <Input
                  id="insured_name"
                  placeholder="e.g., Acme Corporation"
                  {...form.register('insured_name')}
                />
                {form.formState.errors.insured_name && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.insured_name.message}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewSubmission(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Submission'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
